import config from '../../../config/config';
import type { ShopApiErrorBody, ShopErrorCode, ShopErrorDetail } from '../types';

/**
 * Error thrown by every shop API call. `details` is always normalised to an
 * array, even when the server sends a bare object, so consumers never have to
 * branch on the shape.
 */
export class ShopApiError extends Error {
  readonly status: number;
  readonly code: ShopErrorCode;
  readonly details: ShopErrorDetail[];
  readonly body: unknown;

  constructor(
    message: string,
    status: number,
    code: ShopErrorCode,
    details: ShopErrorDetail[] = [],
    body?: unknown
  ) {
    super(message);
    this.name = 'ShopApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.body = body;
    // Required for `instanceof` to work when targeting ES5 (tsconfig target).
    Object.setPrototypeOf(this, ShopApiError.prototype);
  }
}

const KNOWN_CODES: ReadonlySet<string> = new Set<ShopErrorCode>([
  'PRODUCTS_QUERY_FAILED',
  'PRODUCT_NOT_FOUND',
  'PRODUCT_QUERY_FAILED',
  'CATEGORIES_QUERY_FAILED',
  'SETTINGS_QUERY_FAILED',
  'VALIDATION_FAILED',
  'INVALID_JSON',
  'PICKUP_LOCATION_INVALID',
  'PRODUCT_UNAVAILABLE',
  'OUT_OF_STOCK',
  'RATE_LIMITED',
  'CHECKOUT_DISABLED',
  'CHECKOUT_FAILED',
  'NETWORK_ERROR',
  'UNKNOWN',
]);

const toErrorCode = (raw: unknown): ShopErrorCode =>
  typeof raw === 'string' && KNOWN_CODES.has(raw) ? (raw as ShopErrorCode) : 'UNKNOWN';

const normaliseDetails = (raw: unknown): ShopErrorDetail[] => {
  if (Array.isArray(raw)) return raw as ShopErrorDetail[];
  if (raw && typeof raw === 'object') return [raw as ShopErrorDetail];
  return [];
};

/**
 * Builds a query string, dropping undefined/null/empty values so we never
 * send `?q=` or `?minPrice=`.
 */
export const buildQuery = (
  params: Record<string, string | number | boolean | undefined | null>
): string => {
  const search = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return;
    search.append(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export interface ShopFetchInit {
  method?: 'GET' | 'POST';
  body?: unknown;
  /**
   * Optional abort signal.
   *
   * NOTE: the React Query hooks deliberately do NOT pass one. Reading
   * `signal` inside a queryFn sets query-core's internal
   * `#abortSignalConsumed` flag, which makes `removeObserver` call
   * `retryer.cancel()` -> `abortController.abort()` instead of the quieter
   * `cancelRetry()`. Under React 18 StrictMode every mount is
   * mount/unmount/remount, so that aborts each initial request and surfaces
   * "signal is aborted without reason" in the dev overlay.
   *
   * These are small, cached, deduplicated GETs and the search box is
   * debounced, so cancellation buys us very little here. Kept on the
   * signature for callers that genuinely need it (e.g. a future long-running
   * request).
   */
  signal?: AbortSignal;
}

const buildUrl = (path: string) =>
  `${config.shop.apiBaseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;

/**
 * `instanceof DOMException` is unreliable across realms (iframes, some test
 * environments), so match on the name instead.
 */
const isAbortError = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  (err as { name?: string }).name === 'AbortError';

/**
 * Performs the request and hands back the raw Response plus its parsed body.
 * Used directly by callers that need to inspect status/redirect state (the
 * product-by-slug 301 case); everything else goes through `shopFetch`.
 *
 * Parsing is deliberately defensive: an edge/CDN failure can return HTML, and
 * a raw SyntaxError leaking to the UI is useless.
 */
export const shopFetchRaw = async (
  path: string,
  init: ShopFetchInit = {}
): Promise<{ res: Response; body: unknown }> => {
  const { method = 'GET', body, signal } = init;

  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method,
      signal,
      headers: method === 'POST' ? { 'Content-Type': 'application/json' } : undefined,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    // An abort is a cancellation, not a failure — propagate it untouched so
    // React Query recognises it rather than retrying a dead request.
    if (isAbortError(err)) throw err;
    // A CORS rejection is indistinguishable from a network failure here.
    console.warn(
      '[shop] Network request failed. If this is unexpected in production, check ' +
        'that this origin is listed in the backend SHOP_ALLOWED_ORIGINS env var.',
      err
    );
    throw new ShopApiError(
      'Connexion impossible. Vérifiez votre connexion internet.',
      0,
      'NETWORK_ERROR'
    );
  }

  // Reading the body can also abort, so it needs the same treatment.
  let text: string;
  try {
    text = await res.text();
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new ShopApiError(
      'Réponse illisible du serveur.',
      res.status,
      'UNKNOWN'
    );
  }

  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ShopApiError(
        'Réponse illisible du serveur.',
        res.status,
        'UNKNOWN',
        [],
        text.slice(0, 500)
      );
    }
  }

  return { res, body: parsed };
};

/** Throws a ShopApiError built from an error-shaped response body. */
export const throwFromResponse = (res: Response, body: unknown): never => {
  const errorBody = body as Partial<ShopApiErrorBody> | null;
  const error = errorBody?.error;
  throw new ShopApiError(
    error?.message || `Erreur ${res.status}`,
    res.status,
    toErrorCode(error?.code),
    normaliseDetails(error?.details),
    body
  );
};

/** The standard call: parses JSON, throws ShopApiError on any non-2xx. */
export const shopFetch = async <T>(path: string, init: ShopFetchInit = {}): Promise<T> => {
  const { res, body } = await shopFetchRaw(path, init);
  if (!res.ok) throwFromResponse(res, body);
  return body as T;
};
